import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", contextEn: "", contextEs: "", quoteEn: "", quoteEs: "" };

const inputCls = "h-11 bg-white border-sand focus-visible:ring-gold rounded-none text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-1.5";

export const TestimonialForm = ({ open, onOpenChange, testimonial, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(testimonial
        ? { name: testimonial.name, contextEn: testimonial.context?.en || "", contextEs: testimonial.context?.es || "", quoteEn: testimonial.quote?.en || "", quoteEs: testimonial.quote?.es || "" }
        : EMPTY);
    }
  }, [open, testimonial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem("cv_admin_token");
    const payload = { name: form.name, context: { en: form.contextEn, es: form.contextEs }, quote: { en: form.quoteEn, es: form.quoteEs } };
    try {
      if (testimonial?.id) {
        await axios.put(`${API}/admin/testimonials/${testimonial.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t.admin.saved);
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error(t.form.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="testimonial-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">
            {testimonial ? t.admin.editTestimonial : t.admin.addTestimonial}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className={labelCls}>{t.admin.clientName} *</label>
            <Input data-testid="testimonial-name-input" value={form.name} onChange={set("name")} className={inputCls} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t.admin.contextEn}</label>
              <Input data-testid="testimonial-context-en-input" value={form.contextEn} onChange={set("contextEn")} className={inputCls} placeholder="First-time buyer · Kendall" />
            </div>
            <div>
              <label className={labelCls}>{t.admin.contextEs}</label>
              <Input data-testid="testimonial-context-es-input" value={form.contextEs} onChange={set("contextEs")} className={inputCls} placeholder="Comprador primerizo · Kendall" />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t.admin.quoteEn} *</label>
            <Textarea data-testid="testimonial-quote-en-input" rows={3} value={form.quoteEn} onChange={set("quoteEn")} className="bg-white border-sand focus-visible:ring-gold rounded-none text-sm" required />
          </div>
          <div>
            <label className={labelCls}>{t.admin.quoteEs}</label>
            <Textarea data-testid="testimonial-quote-es-input" rows={3} value={form.quoteEs} onChange={set("quoteEs")} className="bg-white border-sand focus-visible:ring-gold rounded-none text-sm" />
          </div>
          <div className="flex gap-3 pt-2">
            <button data-testid="testimonial-save-btn" type="submit" disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-6 py-3.5 transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.admin.save}
            </button>
            <button data-testid="testimonial-cancel-btn" type="button" onClick={() => onOpenChange(false)} className="px-6 py-3.5 border border-sand text-sm font-semibold text-[#525D6B] hover:border-ink hover:text-ink transition-colors">
              {t.admin.cancel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
